import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from '../../styles/GestionInteresadosStyles/GestionInteresados.module.css';
import {
  getDocumentosAsesor,
  subirDocumentoAsesor,
  eliminarDocumentoAsesor,
  getUrlDescargaDocumentoAsesor,
} from '../../api/teachers';

const TIPOS_DOCUMENTO = [
  { key: 'CV', label: 'Curriculum Vitae' },
  { key: 'TITULO', label: 'Título Profesional' },
  { key: 'CEDULA', label: 'Cédula Profesional' },
  { key: 'INE', label: 'INE' },
  { key: 'CURP', label: 'CURP' },
  { key: 'COMPROBANTE_DOMICILIO', label: 'Comprobante de Domicilio' },
  { key: 'RFC', label: 'Constancia de Situación Fiscal (RFC)' },
  { key: 'CONTRATO', label: 'Contrato Laboral' },
];

function ExpedienteAsesorTab({ asesor }) {
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [toast, setToast] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRefs = useRef({});

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const asesorId = asesor?.id_asesor || asesor?.id;

  const cargarDocumentos = useCallback(async () => {
    if (!asesorId) return;
    setLoading(true);
    try {
      const data = await getDocumentosAsesor(asesorId);
      const list = Array.isArray(data) ? data : (data?.results || []);
      setDocumentos(list);
    } catch (err) {
      console.error('Error cargando documentos del asesor:', err);
    } finally {
      setLoading(false);
    }
  }, [asesorId]);

  useEffect(() => {
    cargarDocumentos();
  }, [cargarDocumentos]);

  // Mapear documentos cargados por tipo
  const docPorTipo = {};
  documentos.forEach(doc => {
    docPorTipo[doc.tipo_documento] = doc;
  });

  const handleUploadClick = (tipoKey) => {
    const input = fileInputRefs.current[tipoKey];
    if (input) input.click();
  };

  const handleFileChange = async (tipoKey, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validar PDF
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      showToast('Solo se permiten archivos PDF', 'error');
      event.target.value = '';
      return;
    }

    // Validar tamaño (10 MB)
    if (file.size > 10 * 1024 * 1024) {
      showToast('El archivo no debe superar 10 MB', 'error');
      event.target.value = '';
      return;
    }

    setUploading(tipoKey);
    try {
      await subirDocumentoAsesor(asesorId, tipoKey, file);
      showToast('Documento cargado exitosamente');
      await cargarDocumentos();
    } catch (err) {
      showToast('Error al subir documento: ' + err.message, 'error');
    } finally {
      setUploading(null);
      event.target.value = '';
    }
  };

  const handleViewDocument = (doc) => {
    if (doc.archivo_url) {
      setPreviewUrl(doc.archivo_url);
    } else if (doc.ruta_archivo) {
      const base = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
      const mediaBase = base.replace('/api', '');
      setPreviewUrl(`${mediaBase}/media/${doc.ruta_archivo}`);
    }
  };

  const handleDownloadDocument = (doc) => {
    const token = localStorage.getItem('authToken');
    const url = getUrlDescargaDocumentoAsesor(doc.id_documento);
    fetch(url, {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
    })
      .then(res => {
        if (!res.ok) throw new Error('Error al descargar');
        return res.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = doc.nombre_archivo || 'documento.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch(err => showToast('Error al descargar: ' + err.message, 'error'));
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`¿Eliminar ${doc.nombre_archivo}?`)) return;
    try {
      await eliminarDocumentoAsesor(doc.id_documento);
      showToast('Documento eliminado');
      await cargarDocumentos();
    } catch (err) {
      showToast('Error al eliminar: ' + err.message, 'error');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (!asesorId) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#888' }}>
        Guarde el asesor primero para gestionar su expediente documental.
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 99999,
          padding: '12px 24px', borderRadius: '8px',
          background: toast.type === 'error' ? '#f44336' : '#4caf50',
          color: '#fff', fontWeight: 600, fontSize: '14px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {toast.message}
        </div>
      )}

      {/* Preview Modal */}
      {previewUrl && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)', zIndex: 99998,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setPreviewUrl(null)}
        >
          <div style={{ width: '80%', height: '85%', background: '#fff', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
              <span style={{ fontWeight: 600 }}>Vista previa del documento</span>
              <button type="button" onClick={() => setPreviewUrl(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}>&times;</button>
            </div>
            <iframe
              src={previewUrl}
              title="Vista previa PDF"
              style={{ width: '100%', height: 'calc(100% - 50px)', border: 'none' }}
            />
          </div>
        </div>
      )}

      <div className={styles.documentsContainer || ''}>
        {loading && <div style={{ textAlign: 'center', padding: '16px', color: '#666' }}>Cargando documentos...</div>}

        <div style={{ width: '100%' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px 180px 200px', gap: '8px', padding: '12px 16px', background: '#f5f5f5', borderRadius: '8px 8px 0 0', fontWeight: 600, fontSize: '13px', color: '#555' }}>
            <div>Documento</div>
            <div style={{ textAlign: 'center' }}>Recibido</div>
            <div>Fecha de actualización</div>
            <div style={{ textAlign: 'center' }}>Acciones</div>
          </div>

          {/* Body */}
          <div>
            {TIPOS_DOCUMENTO.map((tipo) => {
              const doc = docPorTipo[tipo.key];
              const recibido = !!doc;
              const isUploading = uploading === tipo.key;

              return (
                <div key={tipo.key} style={{ display: 'grid', gridTemplateColumns: '1fr 100px 180px 200px', gap: '8px', padding: '12px 16px', borderBottom: '1px solid #f0f0f0', alignItems: 'center', fontSize: '14px' }}>
                  <div>
                    {tipo.label}
                    {doc && doc.tamano_bytes ? (
                      <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>({formatSize(doc.tamano_bytes)})</span>
                    ) : null}
                  </div>

                  <div style={{ textAlign: 'center' }}>
                    {recibido ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', background: '#e8f5e9', color: '#4caf50' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20,6 9,17 4,12"/>
                        </svg>
                      </span>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', background: '#fbe9e7', color: '#f44336' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </span>
                    )}
                  </div>

                  <div style={{ color: '#666', fontSize: '13px' }}>
                    {recibido ? formatDate(doc.fecha_actualizacion || doc.fecha_creacion) : '—'}
                  </div>

                  <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                    {/* Input oculto */}
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      style={{ display: 'none' }}
                      ref={el => (fileInputRefs.current[tipo.key] = el)}
                      onChange={(e) => handleFileChange(tipo.key, e)}
                    />

                    {recibido ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleViewDocument(doc)}
                          title="Ver documento"
                          style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#555' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDownloadDocument(doc)}
                          title="Descargar documento"
                          style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#555' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7,10 12,15 17,10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleUploadClick(tipo.key)}
                          title="Reemplazar documento"
                          disabled={isUploading}
                          style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#555' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17,8 12,3 7,8"/>
                            <line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                        </button>

                        <button
                          type="button"
                          onClick={() => handleDeleteDocument(doc)}
                          title="Eliminar documento"
                          style={{ background: 'none', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', color: '#e53935' }}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3,6 5,6 21,6"/>
                            <path d="M19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"/>
                          </svg>
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleUploadClick(tipo.key)}
                        title="Subir archivo PDF"
                        disabled={isUploading}
                        style={{
                          background: '#1976d2', border: 'none', borderRadius: '6px', padding: '6px 16px',
                          cursor: 'pointer', color: '#fff', fontSize: '13px', fontWeight: 500,
                          display: 'flex', alignItems: 'center', gap: '6px', opacity: isUploading ? 0.6 : 1
                        }}
                      >
                        {isUploading ? (
                          <span>Subiendo...</span>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                              <polyline points="17,8 12,3 7,8"/>
                              <line x1="12" y1="3" x2="12" y2="15"/>
                            </svg>
                            Subir PDF
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExpedienteAsesorTab;
